/**
 * §14.2 — instanced rendering architecture.
 *
 * One batch per visual class, per-instance data in preallocated GPU buffers, zero
 * per-frame geometry or material allocation. A batch with no instances this frame is
 * hidden rather than drawn, so the draw-call count tracks what is actually on screen
 * and stays far under the 150 of B7 (§14.2 budgets ≈ 76 at the worst frame).
 *
 * Instances are pushed in ascending `pool_index` order by the caller, which is the
 * determinism contract (§14.3) and also means the buffers are written front-to-back
 * with no gaps and can be uploaded as one range.
 */

import * as THREE from 'three';
import { LINEAR, tokenIndex } from './palette';
import {
  BOSS_IDS,
  ENEMY_DEF_IDS,
  buildBeamGeometry,
  buildBossGeometry,
  buildDiscGeometry,
  buildEnemyGeometry,
  buildFrameGeometry,
  buildHealthGeometry,
  buildMoteGeometry,
  buildPowerupGeometry,
  buildProjectileGeometry,
  buildQuadGeometry,
  buildRingGeometry,
} from './silhouettes';
import { buildPlayerGeometry } from './ship';
import type { ShipFormView } from './ship';
import { VISUAL_CLASS_COUNT, visualClassAdditive, visualClassIsBeam } from './visual-classes';
import { screenWarpUniforms, worldWarpUniforms } from './warp';
import {
  FRAG_BEAM,
  FRAG_BODY,
  FRAG_BOSS_FIELD,
  FRAG_CHEVRON,
  FRAG_GLYPH,
  FRAG_HALO,
  FRAG_HAZARD_ZONE,
  FRAG_GAS,
  FRAG_MOTE,
  FRAG_RADAR,
  FRAG_RING,
  FRAG_SHOCK,
  FRAG_SOLID,
  FRAG_SPRITE,
  FRAG_STORM_VEIL,
  FRAG_STRIPE,
  INSTANCED_VERT,
} from './shaders/instanced';

export const MODE_BODY = 0;
export const MODE_SOLID = 1;
export const MODE_RING = 2;
export const MODE_STRIPE = 3;
export const MODE_SPRITE = 4;
export const MODE_GLYPH = 5;
export const MODE_BEAM = 6;
export const MODE_MOTE = 7;
export const MODE_HALO = 8;
export const MODE_BOSS_FIELD = 9;
export const MODE_HAZARD_ZONE = 10;
/** Retired with AMD-6 D14's world-space chevron (see AMD-8). Slot kept, unused. */
export const MODE_CHEVRON = 11;
export const MODE_SHOCK = 12;
/** AMD-8: the screen-space threat radar and off-screen arrows. See `radar.ts`. */
export const MODE_RADAR = 13;
/** §10.7.4 storm veil. Multiply-blended; see `FRAG_STORM_VEIL`. */
export const MODE_STORM_VEIL = 14;
/** §11.16 drifting gas cloud. See `FRAG_GAS`. */
export const MODE_GAS = 15;

const FRAG_BY_MODE: readonly string[] = [
  FRAG_BODY,
  FRAG_SOLID,
  FRAG_RING,
  FRAG_STRIPE,
  FRAG_SPRITE,
  FRAG_GLYPH,
  FRAG_BEAM,
  FRAG_MOTE,
  FRAG_HALO,
  FRAG_BOSS_FIELD,
  FRAG_HAZARD_ZONE,
  FRAG_CHEVRON,
  FRAG_SHOCK,
  FRAG_RADAR,
  FRAG_STORM_VEIL,
  FRAG_GAS,
];

export type BatchBlend = 'normal' | 'add';

/**
 * §10.7.4 — the storm haze's linear luminance ceiling.
 *
 * AMD-6's hard rule is 0.06 on the background. The veil takes 0.046, three quarters of
 * it, which leaves the floor shader's own two-light budget intact underneath: `mix`
 * never exceeds the larger of its endpoints, so a background pixel under 0.06 composited
 * against a 0.046 haze is still under 0.06, and no amount of storm depth changes that.
 *
 * It is also thirteen times the abyssal floor's 0.0035, which is what makes the storm
 * visible at all — the number is set by what the eye needs, and then checked against the
 * ceiling, rather than the other way round.
 */
export const STORM_HAZE_LUMA = 0.046;

/**
 * §11.9 render order, bottom → top. Values are spread so a layer can be split later
 * without renumbering the ones around it.
 */
export const LAYER_BACKGROUND = 0;
export const LAYER_FLOOR = 10;
export const LAYER_PARALLAX = 20;
/**
 * §11.16 gas clouds, and the render order IS the readability argument.
 *
 * Sitting here — above the parallax field, below the R6 range ring and below every
 * batch that carries a gameplay meaning — is what makes a 20 u coloured wash safe in a
 * game whose whole art direction is about not confusing the player. A cloud can only
 * ever occlude the floor lattice and the background motes. The range ring, the hazard
 * fills, the trails, the glow, the pickups, the bodies, the telegraphs and the player
 * all draw over it at unchanged strength, and they gain contrast from doing so.
 *
 * Nothing about that depends on the colour holding up under measurement, which is the
 * point: AMD-46 found the palette has no hue left to give, so the separation that
 * carries this feature has to be structural.
 */
export const LAYER_GAS = 22;
/**
 * R6 weapon-range ring. On the floor with the sonar lattice and under everything that
 * moves: it is instrumentation the player reads past, so nothing alive may ever be
 * occluded by it.
 */
export const LAYER_RANGE_RING = 24;
export const LAYER_BOSS_FIELD = 28;
export const LAYER_ZONE = 30;
export const LAYER_HAZARD_FILL = 40;
export const LAYER_TRAIL = 45;
/** AMD-6 D4: the halo and bleed layers sit under every body they belong to. */
export const LAYER_GLOW = 48;
export const LAYER_PICKUP = 50;
export const LAYER_ENEMY = 60;
export const LAYER_ENEMY_OUTLINE = 65;
export const LAYER_BOSS = 70;
/**
 * §10.7.4 storm veil, and this number is the whole design decision made concrete.
 *
 * Above it: the floor, the parallax, the gas, the range ring, the boss field, the
 * hazard zones, the trails, the glow, the pickups and every enemy and boss body. Those
 * are what a storm is allowed to take, and it only takes them beyond the 18 u the
 * weapons can reach — see `FRAG_STORM_VEIL`'s protected disc.
 *
 * Below it, and therefore untouched at any storm depth: the player aura, the player,
 * every friendly projectile, the §11.9 telegraph fill and aim line, the particles, the
 * damage numbers and the target indicator. That list is the §14.6 exempt set plus the
 * ship, which is the same list of things the game is not allowed to hide from you, and
 * it is enforced here by an integer rather than by a rule somebody has to remember.
 */
export const LAYER_STORM_VEIL = 74;
export const LAYER_PLAYER_AURA = 78;
export const LAYER_PLAYER = 80;
export const LAYER_FRIENDLY_PROJECTILE = 90;
export const LAYER_TELEGRAPH = 100;
export const LAYER_PARTICLE = 110;
export const LAYER_DAMAGE_NUMBER = 120;
/** §14.6 exempt set: the target indicator draws above everything in the world. */
export const LAYER_INDICATOR = 125;

export interface BatchOptions {
  mode: number;
  blend: BatchBlend;
  renderOrder: number;
  uniforms?: Record<string, THREE.IUniform>;
  name?: string;
  /**
   * AMD-24. `world` (the default) puts the batch through the seam wrap and the curved
   * horizon; `screen` exempts it. The only `screen` batches are the AMD-8 radar plate and
   * marks, which are laid out in output pixels after the composite and would be destroyed
   * by either transform.
   */
  space?: 'world' | 'screen';
}

/**
 * A single instanced draw. `begin()` / `push()` / `end()` is the whole per-frame API and
 * none of it allocates.
 */
export class InstancedBatch {
  readonly mesh: THREE.Mesh;
  readonly capacity: number;
  readonly material: THREE.RawShaderMaterial;

  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly pos: Float32Array;
  private readonly scale: Float32Array;
  private readonly rot: Float32Array;
  private readonly color: Float32Array;
  private readonly params: Float32Array;
  private readonly aPos: THREE.InstancedBufferAttribute;
  private readonly aScale: THREE.InstancedBufferAttribute;
  private readonly aRot: THREE.InstancedBufferAttribute;
  private readonly aColor: THREE.InstancedBufferAttribute;
  private readonly aParams: THREE.InstancedBufferAttribute;
  private n = 0;
  private overflow = 0;

  constructor(base: THREE.BufferGeometry, capacity: number, options: BatchOptions) {
    this.capacity = capacity;

    this.pos = new Float32Array(capacity * 2);
    this.scale = new Float32Array(capacity * 2);
    this.rot = new Float32Array(capacity);
    this.color = new Float32Array(capacity * 3);
    this.params = new Float32Array(capacity * 4);

    this.aPos = new THREE.InstancedBufferAttribute(this.pos, 2);
    this.aScale = new THREE.InstancedBufferAttribute(this.scale, 2);
    this.aRot = new THREE.InstancedBufferAttribute(this.rot, 1);
    this.aColor = new THREE.InstancedBufferAttribute(this.color, 3);
    this.aParams = new THREE.InstancedBufferAttribute(this.params, 4);
    this.aPos.setUsage(THREE.DynamicDrawUsage);
    this.aScale.setUsage(THREE.DynamicDrawUsage);
    this.aRot.setUsage(THREE.DynamicDrawUsage);
    this.aColor.setUsage(THREE.DynamicDrawUsage);
    this.aParams.setUsage(THREE.DynamicDrawUsage);

    this.geometry = new THREE.InstancedBufferGeometry();
    const position = base.getAttribute('position');
    this.geometry.setAttribute('position', position);
    this.geometry.setAttribute('iPos', this.aPos);
    this.geometry.setAttribute('iScale', this.aScale);
    this.geometry.setAttribute('iRot', this.aRot);
    this.geometry.setAttribute('iColor', this.aColor);
    this.geometry.setAttribute('iParams', this.aParams);
    this.geometry.instanceCount = 0;
    // Frustum culling would need a bounding sphere that moves with the instances;
    // the batches are screen-sized anyway, so the test is pure cost.
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e6);

    // The warp uniforms come first so a batch can never shadow them, and they are the
    // SAME IUniform objects on every world batch — one write per frame in `setWarp`
    // moves the whole scene through the seam and onto the sphere.
    const warp = options.space === 'screen' ? screenWarpUniforms() : worldWarpUniforms();

    this.material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: INSTANCED_VERT,
      fragmentShader: FRAG_BY_MODE[options.mode] ?? FRAG_SOLID,
      uniforms: { ...warp, ...(options.uniforms ?? {}) },
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    if (options.blend === 'add') {
      this.material.blending = THREE.CustomBlending;
      this.material.blendEquation = THREE.AddEquation;
      this.material.blendSrc = THREE.SrcAlphaFactor;
      this.material.blendDst = THREE.OneFactor;
      this.material.blendEquationAlpha = THREE.AddEquation;
      this.material.blendSrcAlpha = THREE.OneFactor;
      this.material.blendDstAlpha = THREE.OneFactor;
    } else {
      this.material.blending = THREE.NormalBlending;
    }

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = options.renderOrder;
    this.mesh.visible = false;
    this.mesh.name = options.name ?? `batch-${options.mode}`;
  }

  get count(): number {
    return this.n;
  }

  /** Instances dropped this frame because the batch was full. Feeds the dev overlay. */
  get overflowCount(): number {
    return this.overflow;
  }

  begin(): void {
    this.n = 0;
    this.overflow = 0;
  }

  /** Pushes one instance with a palette token index. Zero allocation. */
  push(
    x: number,
    y: number,
    rot: number,
    sx: number,
    sy: number,
    colorIndex: number,
    alpha: number,
    emissive: number,
    p0: number,
    p1: number,
  ): void {
    const ci = colorIndex * 3;
    this.pushRgb(x, y, rot, sx, sy, LINEAR[ci], LINEAR[ci + 1], LINEAR[ci + 2], alpha, emissive, p0, p1);
  }

  /** Pushes one instance with explicit linear RGB — used where a colour is blended. */
  pushRgb(
    x: number,
    y: number,
    rot: number,
    sx: number,
    sy: number,
    r: number,
    g: number,
    b: number,
    alpha: number,
    emissive: number,
    p0: number,
    p1: number,
  ): void {
    const i = this.n;
    if (i >= this.capacity) {
      this.overflow++;
      return;
    }
    this.pos[i * 2] = x;
    this.pos[i * 2 + 1] = y;
    this.scale[i * 2] = sx;
    this.scale[i * 2 + 1] = sy;
    this.rot[i] = rot;
    this.color[i * 3] = r;
    this.color[i * 3 + 1] = g;
    this.color[i * 3 + 2] = b;
    this.params[i * 4] = alpha;
    this.params[i * 4 + 1] = emissive;
    this.params[i * 4 + 2] = p0;
    this.params[i * 4 + 3] = p1;
    this.n = i + 1;
  }

  /**
   * Swaps the silhouette this batch instances. Boot and run-start only — AMD-43's player
   * hull is rebuilt once per run seed, never per frame, and nothing else calls this.
   *
   * The instance buffers are untouched: same capacity, same attributes, same material, so
   * the batch is still one draw and the shader program does not recompile.
   */
  setBaseGeometry(base: THREE.BufferGeometry): void {
    this.geometry.setAttribute('position', base.getAttribute('position'));
  }

  /** Uploads exactly the range written this frame and toggles the draw off when empty. */
  end(): void {
    const n = this.n;
    this.geometry.instanceCount = n;
    this.mesh.visible = n > 0;
    if (n === 0) return;
    this.mark(this.aPos, n * 2);
    this.mark(this.aScale, n * 2);
    this.mark(this.aRot, n);
    this.mark(this.aColor, n * 3);
    this.mark(this.aParams, n * 4);
  }

  private mark(attr: THREE.InstancedBufferAttribute, count: number): void {
    attr.clearUpdateRanges();
    attr.addUpdateRange(0, count);
    attr.needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * The §14.2 draw-call table, constructed.
 *
 * | Class | Batches |
 * |---|---|
 * | Enemies, one per archetype | 14 |
 * | Elite outline | 1 |
 * | Player + aura | 2 |
 * | Boss body + 2 telegraph overlays | 3 |
 * | Player projectile visual classes | 12 |
 * | Enemy projectile visual classes | 3 |
 * | Pickups (mote, health, power-up) | 3 |
 * | Hazards (circle fill/edge, rect fill/edge, zone ring, status ring) | 6 |
 * | Telegraph overlays (area fill, aim line) | 2 |
 *
 * 46 here; particles add 4, trails 2, damage numbers 1 and the scene 5, for 58 world
 * draws against B7's 150. Empty batches hide themselves, so the resting frame is far
 * lower than the worst frame.
 */
export class BatchRegistry {
  readonly enemies: InstancedBatch[] = [];
  readonly eliteOutline: InstancedBatch;
  /**
   * §8.11 birth iris — the contracting ring that says something is arriving *here*, and
   * in the archetype's own hue, what. §14.6 exempt: it never degrades at any rung.
   */
  readonly birthIris: InstancedBatch;
  readonly player: InstancedBatch;
  readonly playerAura: InstancedBatch;
  /** AMD-6 D4: one merged additive layer carrying every entity's halo and bleed. */
  readonly glow: InstancedBatch;
  /** AMD-6 D6: the 28 px velocity-aligned streak behind every non-beam player shot. */
  readonly streak: InstancedBatch;
  /** AMD-6 D1: rim + capped fill + player punch-out, replacing the violet flood. */
  readonly bossField: InstancedBatch;
  /** One per boss silhouette. Only one boss is ever alive, so this is 1 real draw. */
  readonly bosses: InstancedBatch[] = [];
  readonly bossTelegraphRing: InstancedBatch;
  readonly bossTelegraphLine: InstancedBatch;
  readonly projectiles: InstancedBatch[] = [];
  readonly mote: InstancedBatch;
  readonly health: InstancedBatch;
  readonly powerup: InstancedBatch;
  readonly hazardCircleFill: InstancedBatch;
  readonly hazardCircleEdge: InstancedBatch;
  readonly hazardRectFill: InstancedBatch;
  readonly hazardRectEdge: InstancedBatch;
  readonly zoneRing: InstancedBatch;
  readonly statusRing: InstancedBatch;
  readonly telegraphFill: InstancedBatch;
  readonly telegraphLine: InstancedBatch;
  /** §10.7.4: one multiply-blended quad, the only instance this batch ever carries. */
  readonly stormVeil: InstancedBatch;
  /** §10.7.4: the firebolt's forked stroke. Telegraph-class, so normal blending. */
  readonly stormBolt: InstancedBatch;

  private readonly all: InstancedBatch[] = [];
  /** The geometry `player` is currently instancing. Owned here, so it can be replaced. */
  private playerHull: THREE.BufferGeometry;
  private playerHullSeed = -1;
  /** AMD-50 — `ShipForm.signature` of the hull currently uploaded. */
  private playerFormSig = -1;

  constructor(scene: THREE.Scene, enemyCapacity: number, projectileCapacity: number, pickupCapacity: number) {
    const disc = buildDiscGeometry();
    const ring = buildRingGeometry();
    const quad = buildQuadGeometry();
    const beam = buildBeamGeometry();
    const frame = buildFrameGeometry();

    // §11.9 pins the striped band at 0.40 u; AMD-6 D3 makes that a *world* width rather
    // than a fraction of the zone, so a 40 u hazard shows a border and not a slab.
    const stripeUniforms = (rect: number): Record<string, THREE.IUniform> => ({
      uStripeColor: {
        value: new THREE.Vector3(
          LINEAR[tokenIndex('pal.hazard.stripe') * 3],
          LINEAR[tokenIndex('pal.hazard.stripe') * 3 + 1],
          LINEAR[tokenIndex('pal.hazard.stripe') * 3 + 2],
        ),
      },
      uBand: { value: new THREE.Vector2(0.4, rect) },
    });

    // AMD-6 D4 / AMD-7 R7: each body is three instances in the same batch — a 6 px
    // hue skirt, a 2 px pure-white outline, then the fill. Same geometry, same draw
    // call, so the whole outline treatment costs capacity rather than a batch.
    for (let i = 0; i < ENEMY_DEF_IDS.length; i++) {
      this.enemies.push(
        this.add(scene, new InstancedBatch(buildEnemyGeometry(i), enemyCapacity * 3, {
          mode: MODE_BODY,
          blend: 'normal',
          renderOrder: LAYER_ENEMY,
          name: `enemy-${ENEMY_DEF_IDS[i]}`,
        })),
      );
    }

    this.eliteOutline = this.add(scene, new InstancedBatch(ring, 160, {
      mode: MODE_RING,
      blend: 'normal',
      renderOrder: LAYER_ENEMY_OUTLINE,
      name: 'elite-outline',
    }));

    // §8.11 birth iris. Under the bodies (`LAYER_GLOW - 1`) so a materialising silhouette
    // is never crossed by its own ring, and capacity is generous because a surge wave
    // lands every unit inside one 1.5 s window: 128 concurrent births at max density.
    this.birthIris = this.add(scene, new InstancedBatch(ring, 128, {
      mode: MODE_RING,
      blend: 'normal',
      renderOrder: LAYER_GLOW - 1,
      name: 'birth-iris',
    }));

    for (let i = 0; i < BOSS_IDS.length; i++) {
      this.bosses.push(
        this.add(scene, new InstancedBatch(buildBossGeometry(i), 8, {
          mode: MODE_BODY,
          blend: 'normal',
          renderOrder: LAYER_BOSS,
          name: `boss-${BOSS_IDS[i]}`,
        })),
      );
    }
    this.bossField = this.add(scene, new InstancedBatch(disc, 2, {
      mode: MODE_BOSS_FIELD,
      blend: 'add',
      renderOrder: LAYER_BOSS_FIELD,
      name: 'boss-field',
      uniforms: {
        uPunch: { value: new THREE.Vector3(0, 0, 0.25) },
        // AMD-7 R10: (2 px core, 10 px skirt), rewritten per frame in instance units.
        uRim: { value: new THREE.Vector2(0.006, 0.03) },
      },
    }));
    this.bossTelegraphRing = this.add(scene, new InstancedBatch(ring, 24, {
      mode: MODE_RING,
      blend: 'normal',
      renderOrder: LAYER_TELEGRAPH,
      name: 'boss-telegraph-ring',
    }));
    this.bossTelegraphLine = this.add(scene, new InstancedBatch(beam, 32, {
      mode: MODE_BEAM,
      blend: 'normal',
      renderOrder: LAYER_TELEGRAPH,
      name: 'boss-telegraph-line',
    }));

    this.playerAura = this.add(scene, new InstancedBatch(disc, 2, {
      mode: MODE_HALO,
      blend: 'add',
      renderOrder: LAYER_PLAYER_AURA,
      name: 'player-aura',
    }));
    this.playerHull = buildPlayerGeometry(0);
    this.player = this.add(scene, new InstancedBatch(this.playerHull, 2, {
      mode: MODE_BODY,
      blend: 'normal',
      renderOrder: LAYER_PLAYER,
      name: 'player',
    }));

    // One merged additive layer for every entity's halo and bleed. Capacity covers two
    // instances per on-screen enemy plus one halo per projectile and pickup.
    //
    // A 16-gon, not the 40-gon the rest of the scene uses: the halo's alpha reaches zero
    // at its own rim, so the polygon boundary is never visible, and at ~5,000 instances
    // the difference is 120k triangles a frame.
    this.glow = this.add(scene, new InstancedBatch(buildDiscGeometry(16), enemyCapacity * 2 + projectileCapacity + pickupCapacity, {
      mode: MODE_HALO,
      blend: 'add',
      renderOrder: LAYER_GLOW,
      name: 'entity-glow',
    }));
    this.streak = this.add(scene, new InstancedBatch(beam, projectileCapacity, {
      mode: MODE_BEAM,
      blend: 'add',
      renderOrder: LAYER_FRIENDLY_PROJECTILE - 1,
      name: 'projectile-streak',
    }));

    for (let vc = 0; vc < VISUAL_CLASS_COUNT; vc++) {
      const isBeam = visualClassIsBeam(vc);
      this.projectiles.push(
        this.add(scene, new InstancedBatch(buildProjectileGeometry(vc, isBeam), projectileCapacity, {
          mode: isBeam ? MODE_BEAM : MODE_SOLID,
          blend: visualClassAdditive(vc) ? 'add' : 'normal',
          renderOrder: LAYER_FRIENDLY_PROJECTILE + (visualClassAdditive(vc) ? 1 : 0),
          name: `projectile-vc${vc}`,
        })),
      );
    }

    this.mote = this.add(scene, new InstancedBatch(buildMoteGeometry(), pickupCapacity, {
      mode: MODE_MOTE,
      blend: 'add',
      renderOrder: LAYER_PICKUP,
      name: 'pickup-mote',
    }));
    this.health = this.add(scene, new InstancedBatch(buildHealthGeometry(), 32, {
      mode: MODE_SOLID,
      blend: 'normal',
      renderOrder: LAYER_PICKUP + 1,
      name: 'pickup-health',
    }));
    this.powerup = this.add(scene, new InstancedBatch(buildPowerupGeometry(), 32, {
      mode: MODE_SOLID,
      blend: 'add',
      renderOrder: LAYER_PICKUP + 2,
      name: 'pickup-powerup',
    }));

    // Every hazard and telegraph batch blends normally. §11.4 forbids additive here and
    // §11.10 exempts them from the density-alpha rule; both are contrast guarantees.
    this.hazardCircleFill = this.add(scene, new InstancedBatch(disc, 32, {
      mode: MODE_HAZARD_ZONE,
      blend: 'normal',
      renderOrder: LAYER_HAZARD_FILL,
      name: 'hazard-circle-fill',
      uniforms: { ...stripeUniforms(0), uMetrics: { value: new THREE.Vector3(0.17, 0.67, 1.67) } },
    }));
    this.hazardCircleEdge = this.add(scene, new InstancedBatch(buildRingGeometry(48, 0.8), 32, {
      mode: MODE_STRIPE,
      blend: 'normal',
      renderOrder: LAYER_HAZARD_FILL + 1,
      name: 'hazard-circle-edge',
      uniforms: stripeUniforms(0),
    }));
    this.hazardRectFill = this.add(scene, new InstancedBatch(quad, 32, {
      mode: MODE_HAZARD_ZONE,
      blend: 'normal',
      renderOrder: LAYER_HAZARD_FILL,
      name: 'hazard-rect-fill',
      uniforms: { ...stripeUniforms(1), uMetrics: { value: new THREE.Vector3(0.17, 0.67, 1.67) } },
    }));
    this.hazardRectEdge = this.add(scene, new InstancedBatch(frame, 32, {
      mode: MODE_STRIPE,
      blend: 'normal',
      renderOrder: LAYER_HAZARD_FILL + 1,
      name: 'hazard-rect-edge',
      uniforms: stripeUniforms(1),
    }));
    this.zoneRing = this.add(scene, new InstancedBatch(ring, 48, {
      mode: MODE_RING,
      blend: 'normal',
      renderOrder: LAYER_ZONE,
      name: 'zone-ring',
    }));
    this.statusRing = this.add(scene, new InstancedBatch(ring, 96, {
      mode: MODE_RING,
      blend: 'normal',
      renderOrder: LAYER_ZONE + 1,
      name: 'status-ring',
    }));

    this.telegraphFill = this.add(scene, new InstancedBatch(disc, 64, {
      mode: MODE_SOLID,
      blend: 'normal',
      renderOrder: LAYER_TELEGRAPH,
      name: 'telegraph-fill',
    }));
    this.telegraphLine = this.add(scene, new InstancedBatch(beam, 48, {
      mode: MODE_BEAM,
      blend: 'normal',
      renderOrder: LAYER_TELEGRAPH + 1,
      name: 'telegraph-line',
    }));

    // §10.7.4. Capacity 1: the veil is one screen-covering quad centred on the camera,
    // and a second instance would composite the haze twice and double its depth.
    //
    // `uHaze` is `pal.ui.decor` scaled to `STORM_HAZE_LUMA` — see `FRAG_STORM_VEIL` for
    // why the veil has a colour at all and why that colour has a ceiling.
    const decor = tokenIndex('pal.ui.decor') * 3;
    const decorLuma = Math.max(
      1e-5,
      0.2126 * LINEAR[decor] + 0.7152 * LINEAR[decor + 1] + 0.0722 * LINEAR[decor + 2],
    );
    const hazeScale = STORM_HAZE_LUMA / decorLuma;
    this.stormVeil = this.add(scene, new InstancedBatch(quad, 1, {
      mode: MODE_STORM_VEIL,
      blend: 'normal',
      renderOrder: LAYER_STORM_VEIL,
      name: 'storm-veil',
      uniforms: {
        uEye: { value: new THREE.Vector4(0, 0, 18, 14) },
        uDepth: { value: new THREE.Vector3(0, 0, 0.55) },
        uHaze: {
          value: new THREE.Vector3(
            LINEAR[decor] * hazeScale,
            LINEAR[decor + 1] * hazeScale,
            LINEAR[decor + 2] * hazeScale,
          ),
        },
      },
    }));
    // Four strokes per bolt × `STORM_MAX_BOLTS`, with slack for the flash frame.
    this.stormBolt = this.add(scene, new InstancedBatch(beam, 24, {
      mode: MODE_BEAM,
      blend: 'normal',
      renderOrder: LAYER_TELEGRAPH + 2,
      name: 'storm-bolt',
    }));
  }

  /**
   * AMD-43 — points the player batch at the hull this run's seed generates.
   *
   * Called from `Renderer.install`, which the integrator runs once per run after
   * `world.reset(seed)`. Cached on the seed because `install` also runs after a debug
   * seek, and rebuilding a geometry the GPU already holds would drop and re-upload a
   * buffer for no change. The old hull is disposed AFTER the swap: its position attribute
   * is the one the batch was instancing, and disposing first would free a live buffer.
   */
  setPlayerHull(runSeed: number, form: ShipFormView | null = null): void {
    const seed = runSeed >>> 0;
    // AMD-50 — the hull now answers to the build as well as to the seed, so the cache key is
    // the pair. `form.signature` moves only when something DRAWN moves (a trait point, a
    // weapon glyph, a family's banded level, a bite), which is what keeps a build-reactive
    // silhouette at a handful of buffer uploads per run rather than one per frame.
    const sig = form === null ? 0 : form.signature >>> 0;
    if (seed === this.playerHullSeed && sig === this.playerFormSig) return;
    this.playerHullSeed = seed;
    this.playerFormSig = sig;
    const next = buildPlayerGeometry(seed, form);
    const prev = this.playerHull;
    this.playerHull = next;
    this.player.setBaseGeometry(next);
    prev.dispose();
  }

  private add(scene: THREE.Scene, batch: InstancedBatch): InstancedBatch {
    scene.add(batch.mesh);
    this.all.push(batch);
    return batch;
  }

  /**
   * Makes every batch visible so `WebGLRenderer.compile` will actually compile it.
   *
   * three's `compile()` walks the scene with `traverseVisible`, and a batch constructs
   * itself hidden and is only unhidden by `end()` once it has instances. So the boot
   * compile in `Renderer`'s constructor was silently skipping every batch that does not
   * appear on frame one, and the first frame that showed one paid its shader compile as
   * a single long frame — measured at 215.9 ms when the first storm opened, which the
   * bench reports as `B1 1 frames over 33 ms` plus seven dropped sim steps.
   *
   * `Radar.precompile` has done this for the overlay since AMD-8 and its comment names
   * the same failure. This is the world batches finally getting the same treatment; the
   * storm veil, the bolt and the gas field are what exposed it, but the boss field, the
   * destruction shockwave and the birth iris were all sitting on the same trap.
   */
  setPrecompileVisibility(on: boolean): void {
    for (let i = 0; i < this.all.length; i++) this.all[i].mesh.visible = on;
  }

  beginAll(): void {
    for (let i = 0; i < this.all.length; i++) this.all[i].begin();
  }

  endAll(): void {
    for (let i = 0; i < this.all.length; i++) this.all[i].end();
  }

  /** Batches that will actually issue a draw this frame. */
  get activeCount(): number {
    let n = 0;
    for (let i = 0; i < this.all.length; i++) if (this.all[i].count > 0) n++;
    return n;
  }

  dispose(): void {
    for (const b of this.all) b.dispose();
    this.playerHull.dispose();
  }
}
